import { useRef, useState, useEffect } from 'react'
import { ChevronDown, FileSpreadsheet, FileText } from 'lucide-react'
import { Button } from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import { root, dropdown, item, badge } from './ExportButton.css'

export type ExportFormat = 'xlsx' | 'csv'

interface ExportButtonProps {
  onExport: (format: ExportFormat) => Promise<void>
  disabled?: boolean
}

export function ExportButton({ onExport, disabled = false }: ExportButtonProps) {
  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState<ExportFormat | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  async function pick(format: ExportFormat) {
    setOpen(false)
    setExporting(format)
    try {
      await onExport(format)
    } catch {
      toast.error('Não foi possível exportar. Tente novamente.')
    } finally {
      setExporting(null)
    }
  }

  return (
    <div ref={ref} className={root}>
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || exporting !== null}
        rightIcon={<ChevronDown size={14} />}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {exporting !== null ? 'Exportando…' : 'Exportar'}
      </Button>
      {open && (
        <div className={dropdown} role="menu">
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              void pick('xlsx')
            }}
          >
            <FileSpreadsheet size={15} />
            Planilha Excel
            <span className={badge}>XLSX</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              void pick('csv')
            }}
          >
            <FileText size={15} />
            Arquivo CSV
            <span className={badge}>CSV</span>
          </button>
        </div>
      )}
    </div>
  )
}
